import { FC, useCallback, useEffect } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { validation } from "../../../constants";
import { capitalizeFirstChar } from "../../../utils/helpers/strings";
import { PersonalDetails } from "../../templates/PersonalDetails";
import { Alert } from "../../atoms/Alert";

import { clearUserValues, reauthenticateChangePassword, updateUserRequest } from "../../../redux/user/action";
import { logout } from "../../../redux/auth/actions";
import { clearSpecificRequest } from "../../../redux/request/action";
import {
  REAUTHENTICATE_CHANGE_PASSWORD,
  CHANGE_PASSWORD,
  UPDATE_USER_REQUEST,
} from "../../../redux/user/actionType";
import { SEARCH_APPLICANT_REQUEST } from "../../../redux/applicant/actionTypes";
import { User } from "../../../core/entities/user";

type Props = {};

const PersonalDetailsPage: FC<Props> = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector((state: RootStateOrAny) => state.auth.user);

  const reauthRequest = useSelector(
    (state: RootStateOrAny) =>
      state.requestStore?.[REAUTHENTICATE_CHANGE_PASSWORD]
  );

  const updateRequest = useSelector(
    (state: RootStateOrAny) => state.requestStore?.[UPDATE_USER_REQUEST]
  );

  const updateType = useSelector(
    (state: RootStateOrAny) => state.user.updateType
  );

  const isUpdating = useSelector(
    (state: RootStateOrAny) => state.user.isUpdating
  );
  
  const changePasswordRequest = useSelector(
    (state: RootStateOrAny) => state.requestStore?.[CHANGE_PASSWORD]
  );

  const dispatchUpdatePassword = useCallback(
    (password: string, newPassword: string) =>
      dispatch(reauthenticateChangePassword(password, newPassword)),
    [dispatch]
  );

  const dispatchLogout = useCallback(() => dispatch(logout()), [dispatch]);

  const dispatchClearSpecificRequest = useCallback(
    (request: string) => dispatch(clearSpecificRequest(request)),
    [dispatch]
  );

  const details = {
    fullName: user?.name,
    accessType: capitalizeFirstChar(user?.accessType || ""),
    password: "***********",
    email: user?.email,
  };

  const validate = (values: any) => {
    const errors: any = {};

    if (!values.new_password) {
      errors.new_password = "Please enter your new password!";
    } else if (!validation.passwordValidation.test(values.new_password)) {
      errors.new_password =
        "Password combination must be at least 8 characters, 1 lowercase character, 1 uppercase character, 1 number or special character.";
    }

    if (values.confirm_password !== values.new_password) {
      errors.confirm_password =
        "New Password and Re-entered password do not match";
    }

    return errors;
  };

  useEffect(() => {
    dispatchClearSpecificRequest(SEARCH_APPLICANT_REQUEST);
    return () => {
      dispatch(clearUserValues());
      dispatchClearSpecificRequest(REAUTHENTICATE_CHANGE_PASSWORD);
      dispatchClearSpecificRequest(CHANGE_PASSWORD);
      dispatch(clearSpecificRequest(UPDATE_USER_REQUEST));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (changePasswordRequest?.didSucceed || updateRequest?.didSucceed) {
      setTimeout(() => {
        dispatchLogout();
        history.push("/login");
      }, 1000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, changePasswordRequest?.didSucceed, updateRequest?.didSucceed, history]);

  const handleOnUpdatePassword = (values: any) => {
    dispatchUpdatePassword(values.password, values.new_password);
  };

  const dispatchUpdateUser = useCallback(
    (params: Partial<User>, updateType: string) =>
      dispatch(updateUserRequest(user.id, params, updateType)),
    [dispatch]
  );

  return (
    <>
      <PersonalDetails
        onUpdateAccountDetails={dispatchUpdateUser}
        onUpdatePassword={handleOnUpdatePassword}
        details={details}
        isLoading={reauthRequest?.isLoading || changePasswordRequest?.isLoading}
        validate={validate}
        invalidOldPassword={reauthRequest?.error}
        isUpdating={isUpdating}
        dispatchClearSpecificRequest={dispatchClearSpecificRequest}
      />
       {updateRequest?.error && (
        <Alert type="error" message={updateRequest?.error} showIcon />
      )}
       {!updateRequest?.error && updateType && !isUpdating && (
        <Alert
          type="success"
          message={`Account Details successfully updated`}
          showIcon
        />
      )}
      {changePasswordRequest?.didSucceed && (
        <Alert
          type="success"
          message="Successfully changed password"
          showIcon
        />
      )}
      {changePasswordRequest?.error && (
        <Alert
          type="error"
          message="Password change is unsuccessful"
          showIcon
        />
      )}
    </>
  );
};

export default PersonalDetailsPage;
